/**
 * Money in the currency it was charged in. Never converts USD to GBP.
 * Stripe amounts are minor units (pence, cents).
 */
export type Currency = 'gbp' | 'usd';

export function fmtMinor(minor: number, currency: string, opts: { whole?: boolean } = {}): string {
  const c = currency.toLowerCase();
  const major = minor / 100;
  const symbol = c === 'gbp' ? '£' : c === 'usd' ? '$' : '';
  const digits = opts.whole || major >= 1000 ? 0 : 2;
  const num = major.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return symbol ? `${symbol}${num}` : `${num} ${c.toUpperCase()}`;
}

export function fmtMajor(major: number, currency: string): string {
  return fmtMinor(Math.round(major * 100), currency);
}

/** List prices from the app's supabase/functions/_shared/pricing.ts (26 Oct 2026). Labels only. */
export const LIST_PRICE = {
  uk: { currency: 'gbp' as Currency, monthly: 29.99, pass: 139, pack: 69, premium: 119 },
  us: { currency: 'usd' as Currency, monthly: 44.99, pass: 199, pack: 89, premium: 149 },
};

/** A subscription row as admin-api stripe_overview returns it (legacy or v2 shape). */
export interface StripeSubRow {
  id: string;
  status: string;
  customer?: string;
  email?: string | null;
  cancel_at_period_end?: boolean;
  current_period_end?: string | null;
  amount?: number | null;
  currency?: string | null;
  interval?: string | null;
  items?: { unit_amount?: number | null; currency?: string | null; interval?: string | null; interval_count?: number | null; quantity?: number | null }[];
}

export interface StripeMrr {
  byCurrency: Record<string, { mrrMinor: number; subs: number; monthly: number; yearly: number }>;
  counted: number;
  returned: number;
  pastDue: number;
  possiblyTruncated: boolean;
  unknownPrice: number;
}

/**
 * Monthly recurring revenue per currency from Stripe's own subscription prices.
 * Counts active and trialing subscriptions; yearly prices divided by 12.
 * Coupons and tax are not applied (Stripe's list response does not carry them).
 * admin-api pages through Stripe and sends has_more; an older admin-api sent
 * one page of 100, so without has_more a full page counts as possibly cut short.
 */
export function stripeMrr(rows: StripeSubRow[], hasMore?: boolean): StripeMrr {
  const byCurrency: StripeMrr['byCurrency'] = {};
  let counted = 0, pastDue = 0, unknownPrice = 0;
  for (const r of rows) {
    if (r.status === 'past_due') pastDue += 1;
    if (r.status !== 'active' && r.status !== 'trialing') continue;
    const items = Array.isArray(r.items) && r.items.length
      ? r.items
      : [{ unit_amount: r.amount ?? null, currency: r.currency ?? null, interval: r.interval ?? null, interval_count: 1, quantity: 1 }];
    let any = false;
    for (const i of items) {
      if (i.unit_amount == null || !i.currency || !i.interval) { unknownPrice += 1; continue; }
      const per = (i.interval_count ?? 1) || 1;
      const qty = i.quantity ?? 1;
      const monthly = i.interval === 'year' ? i.unit_amount / (12 * per)
        : i.interval === 'month' ? i.unit_amount / per
        : i.interval === 'week' ? (i.unit_amount * 52) / (12 * per)
        : i.interval === 'day' ? (i.unit_amount * 365) / (12 * per) : 0;
      const c = i.currency.toLowerCase();
      byCurrency[c] = byCurrency[c] ?? { mrrMinor: 0, subs: 0, monthly: 0, yearly: 0 };
      byCurrency[c].mrrMinor += monthly * qty;
      if (!any) {
        byCurrency[c].subs += 1;
        if (i.interval === 'year') byCurrency[c].yearly += 1; else byCurrency[c].monthly += 1;
      }
      any = true;
    }
    if (any) counted += 1;
  }
  return { byCurrency, counted, returned: rows.length, pastDue, possiblyTruncated: hasMore ?? rows.length >= 100, unknownPrice };
}
